import React, { useCallback, useEffect, useRef, useState } from "react";
import { getBuyBoxes, createBuyBox, deleteBuyBox, setBuyBoxEmail } from "../api";
import { BuyBox, BuyBoxEmailFrequency, BuyBoxInput } from "../types";
import { USER_VISIBLE_LEAD_SIGNALS, leadTypeMeta } from "../lib/leadTypeMeta";

// BuyBoxCriteria is the subset of a saved BuyBox the Leads table filters on today
// (counties + acreage live in the model but aren't surfaced in this v1 bar). The
// parent debounces changes before hitting the server.
export interface BuyBoxCriteria {
  signalTypes: string[];
  minValue: number | null;
  maxValue: number | null;
  minEquity: number | null;
  absentee: boolean;
  entity: boolean;
  // #950 — estate/heirs-owned. A distinct outreach signal from absentee:
  // usually multiple decision-makers and an unclear chain of title.
  heir: boolean;
  // #2140 — active foreclosure status AND a code-violation-family signal on
  // the same property (leadTypeMeta's "code_enforcement"/"condemnation").
  // Session-level filter, same posture as `heir`: not yet persisted on a
  // saved BuyBox (would need a column + migration), so a loaded box never
  // constrains on it.
  highConfidenceOnly: boolean;
  // Building criteria (#3061), saved on the box (migration 247). The same
  // rules as the map tab (lib/mapBuyBox.ts): a missing value is kept unless
  // includeUnknownBuilding is off, and land never meets a building criterion.
  minSqft: number | null;
  maxSqft: number | null;
  minYearBuilt: number | null;
  maxYearBuilt: number | null;
  minBeds: number | null;
  minBaths: number | null;
  includeUnknownBuilding: boolean;
  // Land and debt ranges (migration 263). includeUnknownBuilding governs
  // these too: acreage and land value are on about a third of live rows.
  minAcres: number | null;
  maxAcres: number | null;
  minAmountDue: number | null;
  maxAmountDue: number | null;
  minLandValue: number | null;
  maxLandValue: number | null;
}

const LAND_DEBT_KEYS = [
  "minAmountDue",
  "maxAmountDue",
  "minAcres",
  "maxAcres",
  "minLandValue",
  "maxLandValue",
] as const;

export const EMPTY_CRITERIA: BuyBoxCriteria = {
  signalTypes: [],
  minValue: null,
  maxValue: null,
  minEquity: null,
  absentee: false,
  entity: false,
  heir: false,
  highConfidenceOnly: false,
  minSqft: null,
  maxSqft: null,
  minYearBuilt: null,
  maxYearBuilt: null,
  minBeds: null,
  minBaths: null,
  includeUnknownBuilding: true,
  minAcres: null,
  maxAcres: null,
  minAmountDue: null,
  maxAmountDue: null,
  minLandValue: null,
  maxLandValue: null,
};

// The lead reasons a parcel can carry (property_lead_signals.signal_type).
//
// DERIVED, not hand-listed. This was a hardcoded 6-entry copy of a 9-entry
// vocabulary, so `code_enforcement`, `condemnation` and `tax_delinquent` were
// unselectable in the buy box even though the backend accepts all nine
// (buybox_handler.go's buyBoxSignalTypes derives from leadsignals.AllSignalTypes
// precisely because a hand-maintained copy silently rejected two of them —
// #1300). SearchAutocomplete already derives its dropdown this way; this now
// matches, so a new signal type reaches every filter surface at once.
function toSignalOptions(types: readonly string[]): { value: string; label: string }[] {
  return types.map((t) => ({ value: t, label: leadTypeMeta(t).label }));
}

function boxToCriteria(b: BuyBox): BuyBoxCriteria {
  return {
    signalTypes: b.signalTypes || [],
    minValue: b.minAssessedValue,
    maxValue: b.maxAssessedValue,
    minEquity: b.minEquity,
    absentee: b.absenteeOnly,
    entity: b.entityOnly,
    // Not persisted on the buy box: that needs a `heir_only` column on
    // buy_boxes and a migration. A saved box therefore never constrains on
    // it, which is the right default — every existing box predates the
    // filter. The chip still works as a session-level filter.
    heir: false,
    // Same posture as heir above — session-level only, never persisted.
    highConfidenceOnly: false,
    minSqft: b.minSqft ?? null,
    maxSqft: b.maxSqft ?? null,
    minYearBuilt: b.minYearBuilt ?? null,
    maxYearBuilt: b.maxYearBuilt ?? null,
    minBeds: b.minBeds ?? null,
    minBaths: b.minBaths ?? null,
    // Absent on a payload from before migration 247: the default (true).
    includeUnknownBuilding: b.includeUnknownBuilding !== false,
    minAcres: b.minAcres ?? null,
    maxAcres: b.maxAcres ?? null,
    minAmountDue: b.minAmountDue ?? null,
    maxAmountDue: b.maxAmountDue ?? null,
    minLandValue: b.minLandValue ?? null,
    maxLandValue: b.maxLandValue ?? null,
  };
}

function criteriaToInput(name: string, c: BuyBoxCriteria, countyId?: string): BuyBoxInput {
  return {
    name,
    // An empty list means every county, which is what the email alerts would
    // then match. Save the county on screen so a box made while looking at
    // Mecklenburg alerts on Mecklenburg. Leads itself ignores a saved box's
    // counties, so loading is unchanged.
    counties: countyId ? [countyId] : [],
    signalTypes: c.signalTypes,
    propertyClasses: [],
    minAssessedValue: c.minValue,
    maxAssessedValue: c.maxValue,
    minEquity: c.minEquity,
    minAcres: c.minAcres,
    maxAcres: c.maxAcres,
    absenteeOnly: c.absentee,
    entityOnly: c.entity,
    minSqft: c.minSqft,
    maxSqft: c.maxSqft,
    minYearBuilt: c.minYearBuilt,
    maxYearBuilt: c.maxYearBuilt,
    minBeds: c.minBeds,
    minBaths: c.minBaths,
    includeUnknownBuilding: c.includeUnknownBuilding,
    minAmountDue: c.minAmountDue,
    maxAmountDue: c.maxAmountDue,
    minLandValue: c.minLandValue,
    maxLandValue: c.maxLandValue,
  };
}

// parseNum turns an input string into number|null (empty/invalid = no bound).
function parseNum(s: string): number | null {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

interface BuyBoxBarProps {
  criteria: BuyBoxCriteria;
  onChange: (c: BuyBoxCriteria) => void;

  /**
   * Priority-ordered signal types actually present in the county(ies) this
   * buy box currently applies to (#2257) — parent computes this with
   * leadTypeMeta.ts's computeAvailableSignalTypes from the leads already
   * fetched for the table this bar sits above. Only narrows the checkbox
   * PANEL (what a user can newly pick); a value already in
   * `criteria.signalTypes` (e.g. loaded from a saved box, or left over from
   * a county switch) still renders as a removable chip via signalLabel's
   * full-vocabulary lookup, so switching counties never silently discards a
   * saved filter's meaning. Optional: defaults to the full
   * USER_VISIBLE_LEAD_SIGNALS list so scout-tier (statewide, no single
   * county) and any caller that hasn't threaded this through keep the
   * pre-#2257 behavior.
   */
  availableSignalTypes?: string[];

  /**
   * Notification bell deep link (#2302): select and apply this saved box once
   * the box list has loaded. `n` distinguishes a repeat request for the same
   * box. An id that no longer exists (box deleted since the count) is ignored.
   */
  boxRequest?: { id: string; n: number };

  /**
   * Max saved searches for this viewer's plan (Scout: 15). Omit for unlimited.
   * The server enforces it on create; this only shows "n/15" and disables Save
   * at the limit so nobody types a name and hits a refusal.
   */
  savedLimit?: number;

  /**
   * County to save new boxes for. Omit for a viewer whose leads already span
   * their chosen counties (Scout, where the server scopes the box anyway).
   */
  saveCountyId?: string;
}

// BuyBoxBar renders the "these are the deals I want" filter controls plus a
// saved-box selector. Discrete toggles (signals / absentee / entity) apply
// immediately; the parent debounces the resulting fetch. Pro-gated server-side —
// a 403 on load just leaves the saved-box list empty.
const BuyBoxBar: React.FC<BuyBoxBarProps> = ({
  criteria,
  onChange,
  availableSignalTypes = USER_VISIBLE_LEAD_SIGNALS,
  boxRequest,
  savedLimit,
  saveCountyId,
}) => {
  const signalOptions = toSignalOptions(availableSignalTypes);
  const [boxes, setBoxes] = useState<BuyBox[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [saving, setSaving] = useState(false);
  // Signals now live in a single checkbox dropdown (was a wrapping row of
  // per-signal buttons — cramped once the vocabulary grew to nine).
  const [signalsOpen, setSignalsOpen] = useState(false);
  const signalsRef = useRef<HTMLDivElement>(null);
  // Naming a saved buy box happens inline, in-app (was a native window.prompt).
  const [saveMode, setSaveMode] = useState(false);
  const [saveName, setSaveName] = useState("My buy box");

  const loadBoxes = useCallback(async () => {
    try {
      setBoxes(await getBuyBoxes());
    } catch {
      setBoxes([]); // 403 for non-Pro, or transient — no saved boxes to show
    }
  }, []);

  useEffect(() => {
    loadBoxes();
  }, [loadBoxes]);

  // Close the signals dropdown on an outside click or Escape.
  useEffect(() => {
    if (!signalsOpen) return;
    const onDown = (e: MouseEvent) => {
      if (signalsRef.current && !signalsRef.current.contains(e.target as Node)) setSignalsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSignalsOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [signalsOpen]);

  const toggleSignal = (value: string) => {
    const has = criteria.signalTypes.includes(value);
    onChange({
      ...criteria,
      signalTypes: has
        ? criteria.signalTypes.filter((s) => s !== value)
        : [...criteria.signalTypes, value],
    });
  };

  // Full-vocabulary lookup (not the county-scoped signalOptions above) — a
  // chip for an already-selected signal must keep showing its real label
  // even after a county switch narrows availableSignalTypes out from under
  // it (see availableSignalTypes' doc comment).
  const signalLabel = (value: string): string => leadTypeMeta(value).label;

  const handleLoad = (id: string) => {
    setSelectedId(id);
    if (!id) {
      onChange(EMPTY_CRITERIA);
      return;
    }
    const box = boxes.find((b) => b.id === id);
    if (box) onChange(boxToCriteria(box));
  };

  // Apply a bell-requested box once it is in the loaded list (see boxRequest).
  const handledRequestRef = useRef<number | null>(null);
  useEffect(() => {
    if (!boxRequest || handledRequestRef.current === boxRequest.n) return;
    const box = boxes.find((b) => b.id === boxRequest.id);
    if (!box) return;
    handledRequestRef.current = boxRequest.n;
    setSelectedId(box.id);
    onChange(boxToCriteria(box));
    // onChange is the parent's state setter; re-running on its identity would
    // re-apply the box over the user's later edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxRequest, boxes]);

  const openSave = () => {
    setSaveName("My buy box");
    setSaveMode(true);
  };
  const cancelSave = () => setSaveMode(false);

  const handleSave = async () => {
    const name = saveName.trim() || "My buy box";
    setSaving(true);
    try {
      const created = await createBuyBox(criteriaToInput(name, criteria, saveCountyId));
      await loadBoxes();
      setSelectedId(created.id);
      setSaveMode(false);
    } catch (err: any) {
      if (err?.response?.status === 403 && err?.response?.data?.code === "buy_box_cap_reached") {
        // eslint-disable-next-line no-alert
        window.alert(
          `You've saved ${err.response.data.limit ?? savedLimit ?? "the maximum"} searches, the most your plan allows. ` +
            "Delete one, or upgrade to Pro for unlimited."
        );
      } else {
        // eslint-disable-next-line no-alert
        window.alert("Couldn't save this buy box. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await deleteBuyBox(selectedId);
      setSelectedId("");
      await loadBoxes();
    } catch {
      // eslint-disable-next-line no-alert
      window.alert("Couldn't delete this buy box.");
    } finally {
      setSaving(false);
    }
  };

  const clearAll = () => {
    setSelectedId("");
    onChange(EMPTY_CRITERIA);
  };

  // Email alerts for the selected box (#3062). Optimistic; reverted on error.
  const selectedBox = boxes.find((b) => b.id === selectedId);
  const handleEmailChange = async (frequency: BuyBoxEmailFrequency) => {
    if (!selectedBox) return;
    const prev = selectedBox.emailFrequency ?? "off";
    const setFreq = (f: BuyBoxEmailFrequency) =>
      setBoxes((bs) => bs.map((b) => (b.id === selectedBox.id ? { ...b, emailFrequency: f } : b)));
    setFreq(frequency);
    try {
      await setBuyBoxEmail(selectedBox.id, frequency);
    } catch {
      setFreq(prev);
      // eslint-disable-next-line no-alert
      window.alert("Couldn't change email alerts for this buy box. Please try again.");
    }
  };

  const atLimit = savedLimit !== undefined && boxes.length >= savedLimit;

  const hasCriteria =
    criteria.signalTypes.length > 0 ||
    criteria.minValue != null ||
    criteria.maxValue != null ||
    criteria.minEquity != null ||
    criteria.absentee ||
    criteria.entity ||
    criteria.heir ||
    criteria.highConfidenceOnly ||
    criteria.minSqft != null ||
    criteria.maxSqft != null ||
    criteria.minYearBuilt != null ||
    criteria.maxYearBuilt != null ||
    criteria.minBeds != null ||
    criteria.minBaths != null ||
    LAND_DEBT_KEYS.some((k) => criteria[k] != null) ||
    !criteria.includeUnknownBuilding;

  type LandDebtKey = (typeof LAND_DEBT_KEYS)[number];
  const landDebtInput = (key: LandDebtKey, placeholder: string, label: string) => (
    <input
      className="lv-chip bbx-num"
      type="number"
      inputMode="numeric"
      placeholder={placeholder}
      value={criteria[key] ?? ""}
      onChange={(e) => onChange({ ...criteria, [key]: parseNum(e.target.value) })}
      aria-label={label}
    />
  );

  type BuildingKey = "minSqft" | "maxSqft" | "minYearBuilt" | "maxYearBuilt" | "minBeds" | "minBaths";
  const buildingInput = (key: BuildingKey, placeholder: string, label: string) => (
    <input
      className="lv-chip bbx-num"
      type="number"
      inputMode="numeric"
      placeholder={placeholder}
      value={criteria[key] ?? ""}
      onChange={(e) => onChange({ ...criteria, [key]: parseNum(e.target.value) })}
      aria-label={label}
    />
  );

  return (
    <div className="bbx" data-tour="buy-box">
      <div className="bbx-row">
        <span className="bbx-label">Signals</span>
        <div className="bbx-dd" ref={signalsRef}>
          <button
            type="button"
            className={"lv-chip bbx-dd-btn" + (signalsOpen ? " bbx-dd-btn--open" : "")}
            aria-haspopup="true"
            aria-expanded={signalsOpen}
            onClick={() => setSignalsOpen((o) => !o)}
          >
            Signals
            <span className={"bbx-dd-count" + (criteria.signalTypes.length ? "" : " bbx-dd-count--zero")}>
              {criteria.signalTypes.length || "0"}
            </span>
            <span className="bbx-dd-caret" aria-hidden="true">
              ▾
            </span>
          </button>
          {signalsOpen && (
            <div className="bbx-dd-panel" role="group" aria-label="Lead signals">
              {signalOptions.map((o) => (
                <label key={o.value} className="bbx-dd-opt">
                  <input
                    type="checkbox"
                    checked={criteria.signalTypes.includes(o.value)}
                    onChange={() => toggleSignal(o.value)}
                  />
                  <span>{o.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        {criteria.signalTypes.map((v) => (
          <span key={v} className="bbx-sig-chip">
            {signalLabel(v)}
            <button type="button" aria-label={`Remove ${signalLabel(v)}`} onClick={() => toggleSignal(v)}>
              ×
            </button>
          </span>
        ))}
      </div>

      <div className="bbx-row">
        <span className="bbx-label">Buy box</span>
        <input
          className="lv-chip bbx-num"
          type="number"
          inputMode="numeric"
          placeholder="Min value"
          value={criteria.minValue ?? ""}
          onChange={(e) => onChange({ ...criteria, minValue: parseNum(e.target.value) })}
          aria-label="Minimum assessed value"
        />
        <input
          className="lv-chip bbx-num"
          type="number"
          inputMode="numeric"
          placeholder="Max value"
          value={criteria.maxValue ?? ""}
          onChange={(e) => onChange({ ...criteria, maxValue: parseNum(e.target.value) })}
          aria-label="Maximum assessed value"
        />
        <input
          className="lv-chip bbx-num"
          type="number"
          inputMode="numeric"
          placeholder="Min equity"
          value={criteria.minEquity ?? ""}
          onChange={(e) => onChange({ ...criteria, minEquity: parseNum(e.target.value) })}
          aria-label="Minimum estimated equity"
        />
        <button
          type="button"
          className={"lv-chip" + (criteria.absentee ? " lv-chip--on" : "")}
          onClick={() => onChange({ ...criteria, absentee: !criteria.absentee })}
        >
          Absentee
        </button>
        <button
          type="button"
          className={"lv-chip" + (criteria.entity ? " lv-chip--on" : "")}
          onClick={() => onChange({ ...criteria, entity: !criteria.entity })}
        >
          LLC-owned
        </button>
        <button
          type="button"
          className={"lv-chip" + (criteria.heir ? " lv-chip--on" : "")}
          onClick={() => onChange({ ...criteria, heir: !criteria.heir })}
        >
          Estate
        </button>
        <button
          type="button"
          className={"lv-chip" + (criteria.highConfidenceOnly ? " lv-chip--on" : "")}
          onClick={() => onChange({ ...criteria, highConfidenceOnly: !criteria.highConfidenceOnly })}
          title="Active foreclosure status AND a code-violation/condemnation signal on the same property"
        >
          High-Confidence
        </button>
        {hasCriteria && (
          <button type="button" className="lv-chip bbx-clear" onClick={clearAll}>
            Clear
          </button>
        )}
      </div>

      <div className="bbx-row">
        <span className="bbx-label">Building</span>
        {buildingInput("minSqft", "Min sq ft", "Minimum square feet")}
        {buildingInput("maxSqft", "Max sq ft", "Maximum square feet")}
        {buildingInput("minYearBuilt", "Built from", "Built in or after")}
        {buildingInput("maxYearBuilt", "Built to", "Built in or before")}
        {buildingInput("minBeds", "Beds", "Minimum bedrooms")}
        {buildingInput("minBaths", "Baths", "Minimum bathrooms")}
        <button
          type="button"
          className={"lv-chip" + (criteria.includeUnknownBuilding ? " lv-chip--on" : "")}
          aria-pressed={criteria.includeUnknownBuilding}
          onClick={() => onChange({ ...criteria, includeUnknownBuilding: !criteria.includeUnknownBuilding })}
          title="Many counties don't publish every building fact. On: a property missing a value still counts."
        >
          Include missing data
        </button>
      </div>

      <div className="bbx-row">
        <span className="bbx-label">Land &amp; debt</span>
        {landDebtInput("minAmountDue", "Owed min $", "Minimum amount owed")}
        {landDebtInput("maxAmountDue", "Owed max $", "Maximum amount owed")}
        {landDebtInput("minAcres", "Acres min", "Minimum acres")}
        {landDebtInput("maxAcres", "Acres max", "Maximum acres")}
        {landDebtInput("minLandValue", "Land min $", "Minimum land value")}
        {landDebtInput("maxLandValue", "Land max $", "Maximum land value")}
      </div>

      <div className="bbx-row">
        <span className="bbx-label">Saved</span>
        <select
          className="lv-chip"
          value={selectedId}
          onChange={(e) => handleLoad(e.target.value)}
          aria-label="Load a saved buy box"
        >
          <option value="">— Buy box —</option>
          {boxes.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        {savedLimit !== undefined && (
          <span
            className="bbx-count"
            aria-label={`${boxes.length} of ${savedLimit} saved searches used`}
            title={`${boxes.length} of ${savedLimit} saved searches used`}
          >
            {boxes.length}/{savedLimit}
          </span>
        )}
        {saveMode ? (
          <span className="bbx-save">
            <input
              className="bbx-save-input"
              autoFocus
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
                if (e.key === "Escape") cancelSave();
              }}
              placeholder="Name this buy box"
              aria-label="Buy box name"
              disabled={saving}
            />
            <button type="button" className="lv-chip bbx-save-ok" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="bbx-save-x"
              onClick={cancelSave}
              disabled={saving}
              aria-label="Cancel"
            >
              ×
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="lv-chip"
            onClick={openSave}
            disabled={!hasCriteria || atLimit}
            title={
              atLimit
                ? `You've used all ${savedLimit} saved searches. Delete one to save another.`
                : undefined
            }
          >
            Save as…
          </button>
        )}
        {selectedBox && !saveMode && (
          <select
            className="lv-chip"
            value={selectedBox.emailFrequency ?? "off"}
            onChange={(e) => handleEmailChange(e.target.value as BuyBoxEmailFrequency)}
            aria-label="Email me new matches for this buy box"
            title="Get one email with this box's new matches. Nothing is sent when there are none."
          >
            <option value="off">Email: off</option>
            <option value="daily">Email: daily</option>
            <option value="weekly">Email: weekly</option>
          </select>
        )}
        {selectedId && !saveMode && (
          <button type="button" className="lv-chip bbx-clear" onClick={handleDelete} disabled={saving}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

export default BuyBoxBar;
